import { API_URL, API_BASE_URL } from "../config/api";
import { uniqueThreadMessages } from "../multiuser/messagingLogic";

const STORAGE_KEY = "vocabularium_kingdom_mail";

export const ACCOUNT_PURGED_EVENT = "vocabularium-account-purged";
export const PRESENCE_CHANGED_EVENT = "vocabularium-presence-changed";

function appearOfflineStorageKey(userId) {
  return `vocabularium_admin_appear_offline:${String(userId || "").trim()}`;
}

export function readAdminAppearOffline(userId) {
  if (!userId || typeof window === "undefined") return false;
  try {
    return localStorage.getItem(appearOfflineStorageKey(userId)) === "1";
  } catch {
    return false;
  }
}

export function writeAdminAppearOffline(userId, hidden) {
  if (!userId || typeof window === "undefined") return;
  try {
    localStorage.setItem(appearOfflineStorageKey(userId), hidden ? "1" : "0");
  } catch {
    // quota / mode privé
  }
}

export function notifyAccountPurged(userId) {
  if (typeof window === "undefined") return;
  const id = String(userId || "").trim();
  if (!id) return;
  window.dispatchEvent(new CustomEvent(ACCOUNT_PURGED_EVENT, { detail: { userId: id } }));
}

export function readMailWindowState() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!parsed || typeof parsed !== "object") return {};
    return {
      leftRatio: Number.isFinite(parsed.leftRatio) ? parsed.leftRatio : undefined,
      topRatio: Number.isFinite(parsed.topRatio) ? parsed.topRatio : undefined,
      listOpen: parsed.listOpen !== false,
      selectedThreadId:
        typeof parsed.selectedThreadId === "string" ? parsed.selectedThreadId : "",
    };
  } catch {
    return {};
  }
}

export function writeMailWindowState(next) {
  try {
    const current = readMailWindowState();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...next }));
  } catch {
    // quota / mode privé
  }
}

function messagingApiOrigin() {
  const explicit = String(process.env.REACT_APP_API_BASE_URL || "").trim();
  if (explicit) {
    try {
      return new URL(explicit).origin;
    } catch {
      // ignore
    }
  }
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return "https://api.vocabularium.fr";
  }
  if (API_BASE_URL && !/127\.0\.0\.1|localhost/.test(API_BASE_URL)) {
    try {
      return new URL(API_BASE_URL, window.location.origin).origin;
    } catch {
      // ignore
    }
  }
  return window.location.origin;
}

export async function openMessagingSocket({ signal, appearOffline = false } = {}) {
  const res = await fetch(`${API_URL}/messaging/socket-ticket`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ appearOffline: appearOffline === true }),
    signal,
  });
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
  const json = await readJson(res);
  const ticket = String(json.ticket || "").trim();
  if (!/^[a-f0-9]{64}$/.test(ticket)) {
    throw new Error("Billet WebSocket invalide.");
  }
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
  const origin = messagingApiOrigin();
  const url = new URL("/apivocabularium/messaging/ws", origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("ticket", ticket);
  return new WebSocket(url.toString());
}

async function readJson(res) {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(json.message || "Erreur de messagerie.");
    error.status = res.status;
    throw error;
  }
  return json;
}

export async function fetchMessagingBootstrap(blasonIndex, { fresh = false } = {}) {
  const params = new URLSearchParams();
  if (Number.isInteger(blasonIndex) && blasonIndex >= 0) {
    params.set("blasonIndex", String(blasonIndex));
  }
  if (fresh) params.set("fresh", "1");
  const query = params.toString();
  const res = await fetch(`${API_URL}/messaging/bootstrap${query ? `?${query}` : ""}`);
  return readJson(res);
}

export async function postMessagingAppearance(blasonIndex) {
  const body = {};
  if (Number.isInteger(blasonIndex) && blasonIndex >= 0) {
    body.blasonIndex = blasonIndex;
  }
  const res = await fetch(`${API_URL}/messaging/appearance`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return readJson(res);
}

export async function fetchThreadMessages(threadId) {
  const res = await fetch(
    `${API_URL}/messaging/threads/${encodeURIComponent(threadId)}/messages`
  );
  const json = await readJson(res);
  return {
    ...json,
    messages: uniqueThreadMessages(json.messages),
  };
}

export async function postThreadMessage(threadId, body) {
  const res = await fetch(
    `${API_URL}/messaging/threads/${encodeURIComponent(threadId)}/messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }
  );
  return readJson(res);
}

export async function fetchOfficialCatalog() {
  const res = await fetch(`${API_URL}/admin/messaging/official`, {
    credentials: "include",
  });
  return readJson(res);
}

export async function saveOfficialWelcome(body) {
  const res = await fetch(`${API_URL}/admin/messaging/official/welcome`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  return readJson(res);
}

export async function publishOfficialAnnouncement(body, channel = "official") {
  const res = await fetch(`${API_URL}/admin/messaging/official`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body, channel: channel === "news" ? "news" : "official" }),
  });
  return readJson(res);
}

export async function updateOfficialAnnouncement(announcementId, body) {
  const res = await fetch(
    `${API_URL}/admin/messaging/official/${encodeURIComponent(announcementId)}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }
  );
  return readJson(res);
}

export async function deleteOfficialAnnouncement(announcementId) {
  const res = await fetch(
    `${API_URL}/admin/messaging/official/${encodeURIComponent(announcementId)}`,
    { method: "DELETE", credentials: "include" }
  );
  return readJson(res);
}

export async function fetchCitizens() {
  const res = await fetch(`${API_URL}/citizens`, { credentials: "include" });
  return readJson(res);
}

export async function saveCitizenCard(card) {
  const res = await fetch(`${API_URL}/citizens/me`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(card),
  });
  return readJson(res);
}

export async function markThreadRead(threadId) {
  const res = await fetch(
    `${API_URL}/messaging/threads/${encodeURIComponent(threadId)}/read`,
    { method: "POST" }
  );
  return readJson(res);
}

export function formatMessageStamp(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  const datePart = date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return `${datePart} · ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function lastReceivedLabel(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `dernier message reçu à ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function visibleUnreadForContact(contact, { windowOpen, selectedThreadId } = {}) {
  if (!contact) return 0;
  if (windowOpen && selectedThreadId && contact.threadId === selectedThreadId) return 0;
  return Number(contact.unread) || 0;
}

export function visibleUnreadTotalForViewer({
  isAdmin,
  contacts,
  windowOpen,
  selectedThreadId,
} = {}) {
  const list = Array.isArray(contacts) ? contacts : [];
  return list.reduce((sum, contact) => {
    const unread = visibleUnreadForContact(contact, {
      windowOpen,
      selectedThreadId,
    });
    if (isAdmin && contact.kind !== "conversation") return sum;
    return sum + unread;
  }, 0);
}
